import React, { useEffect, useRef, useState } from 'react';
import { Upload, Trash2, FileText, Database, Mail } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import { lettersService } from '../../services/lettersService';
import { LEGACY_LETTERS } from '../../data/legacyData';
import { formatFullDate, toIsoDate } from '../../utils/format';

function LettersTab({ onStatus, onNotifyLetter }) {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState({ title: '', date: '', type: 'Quarterly' });
  const fileInputRef = useRef(null);

  useEffect(() => {
    const unsub = lettersService.subscribeToLetters(
      (data) => {
        setLetters(data);
        setLoading(false);
      },
      (err) => {
        onStatus(`Error loading letters: ${err.message}`, true);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [onStatus]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!meta.title || !meta.date) {
      onStatus('Set a title and date before choosing a file.', true);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    try {
      setUploading(true);
      onStatus('Uploading PDF...');
      await lettersService.uploadLetter(file, meta);
      setMeta({ title: '', date: '', type: 'Quarterly' });
      onStatus('Letter uploaded.');
    } catch (err) {
      onStatus(`Upload error: ${err.message}`, true);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const remove = async (letter) => {
    if (!window.confirm(`Delete "${letter.title}"?`)) return;
    try {
      await lettersService.deleteLetter(letter);
      onStatus('Letter deleted.');
    } catch (err) {
      onStatus(`Error deleting: ${err.message}`, true);
    }
  };

  const seedLegacy = async () => {
    if (!window.confirm('Add the existing historical letters to the database? Run once.')) return;
    try {
      onStatus('Seeding historical letters...');
      for (const l of LEGACY_LETTERS) {
        await lettersService.addLetterByUrl(l);
      }
      onStatus(`Seeded ${LEGACY_LETTERS.length} historical letters.`);
    } catch (err) {
      onStatus(`Seed error: ${err.message}`, true);
    }
  };

  if (loading) return <LoadingSpinner text="Loading letters..." />;

  return (
    <div>
      <div className="a-toolbar">
        <h2 className="a-section-title">Published letters</h2>
        <div className="a-toolbar-actions">
          {letters.length === 0 && (
            <button className="a-btn a-btn-ghost" onClick={seedLegacy}>
              <Database size={16} /> Seed historical letters
            </button>
          )}
        </div>
      </div>

      <div className="a-card a-card-pad" style={{ marginBottom: 18 }}>
        <div className="a-form-grid">
          <div className="a-field">
            <label>Title</label>
            <input
              className="a-input"
              type="text"
              value={meta.title}
              onChange={(e) => setMeta({ ...meta, title: e.target.value })}
              placeholder="e.g., Q1 2025 Update"
            />
          </div>
          <div className="a-field">
            <label>Date</label>
            <input
              className="a-input"
              type="date"
              value={toIsoDate(meta.date)}
              onChange={(e) => setMeta({ ...meta, date: e.target.value })}
            />
          </div>
          <div className="a-field">
            <label>Type</label>
            <select
              className="a-select"
              value={meta.type}
              onChange={(e) => setMeta({ ...meta, type: e.target.value })}
            >
              <option value="Quarterly">Quarterly</option>
              <option value="Annual">Annual</option>
              <option value="Special">Special</option>
            </select>
          </div>
          <button
            className="a-btn a-btn-primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <Upload size={16} /> {uploading ? 'Uploading...' : 'Choose PDF & upload'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handleUpload}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {letters.length === 0 ? (
        <div className="a-card a-empty">
          <FileText size={30} />
          <p>No letters yet. Upload a PDF or seed the historical set.</p>
        </div>
      ) : (
        <div className="a-card">
          {letters.map((letter) => (
            <div key={letter.id} className="a-letter-row">
              <div className="a-letter-icon">
                <FileText size={20} />
              </div>
              <div className="a-letter-info">
                <a href={letter.url} target="_blank" rel="noopener noreferrer">
                  {letter.title}
                </a>
                <span className="a-letter-meta">
                  {letter.type} &middot; {formatFullDate(letter.date)}
                </span>
              </div>
              <div className="a-letter-row-actions">
                {onNotifyLetter && (
                  <button
                    type="button"
                    className="a-icon-btn"
                    onClick={() =>
                      onNotifyLetter({
                        subject: `New investor update: ${letter.title}`,
                        body: `A new update is now available on the investor portal.\n\n${letter.title}\n\nYou can view all letters and reports by signing in at the link below.`,
                      })
                    }
                    title="Email investors about this letter"
                  >
                    <Mail size={15} />
                  </button>
                )}
                <button className="a-icon-btn danger" onClick={() => remove(letter)} title="Delete">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default LettersTab;
